import { useState, useEffect, useRef, useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { recordStopChange } from '../../lib/stopModule'
import { useOrderGateway } from '../../hooks/useOrderGateway'
import { useWorkingOrders } from '../../hooks/useWorkingOrders'
import { useAccountNicknames } from '../../hooks/useAccountNicknames'
import { useBiometricGate } from '../../hooks/useBiometricGate'
import { useAuth } from '../../hooks/useAuth'
import OrderConfirmationModal from '../OrderConfirmationModal'
import { isSplitBracketPosition, hasBrokerShapeSplitBracket, isUnsupportedSplitExitTopology } from '../../lib/bracketOrderGroups'
import { buildCloseFullPositionPlan, buildMoveStopReplacePlan, buildTakeProfitScaleOutPlan } from '../../lib/splitQuickActionPlans'
import { groupBuyExecutionsIntoTranches, matchStopGroupsToBuyTranches } from '../../lib/trancheStopMatcher'

// EF-190: legacy quick-action keys gated by the split-bracket detector.
// Centralized so the router-level and handler-level guards share one source
// of truth; adding a fourth key in a future slice only touches this set.
const GUARDED_LEGACY_QUICK_ACTION_KEYS = new Set(['take_profit', 'move_stop', 'close_pos'])
const UNSUPPORTED_SPLIT_GUARD_MESSAGE =
  'Unsupported split exit detected. Manage exits in TOS until this order shape is supported.'
const SPLIT_BRACKET_PLAN_BLOCKED_MESSAGE =
  'Split-bracket exit active. Some quick actions are disabled until stop and target coverage can be verified.'

// requiresOrders=true means the action needs the working-orders cache to be
// populated before it can run (everything that touches stops). Until the
// initial fetch chain completes, those buttons stay disabled to avoid the
// enabled→disabled→enabled flash that used to happen mid-render.
const ACTIONS = [
  { key: 'take_profit',  label: 'Take 1/3 Profit',     icon: '\u2197', accent: 'border-l-[rgb(var(--color-forest))]',  enabled: true,  requiresOrders: true  },
  { key: 'take_half',    label: 'Take 1/2 Remaining',  icon: '\u2197', accent: 'border-l-[rgb(var(--color-forest))]',  enabled: false, requiresOrders: true  },
  { key: 'move_stop',    label: 'Move Stop',           icon: '\u21C5', accent: 'border-l-[#c4953a]',              enabled: true,  requiresOrders: true  },
  { key: 'add_shares',   label: 'Add Shares',          icon: '+', accent: 'border-l-[#4a7fb5]',              enabled: false, requiresOrders: false },
  { key: 'option_hedge', label: 'Add Option Hedge',    icon: '\uD83D\uDEE1', accent: 'border-l-[#8b5fc7]',             enabled: false, requiresOrders: false },
  { key: 'inverse_hedge',label: 'Inverse ETF Hedge',   icon: '\u2193', accent: 'border-l-[#8b5fc7]',              enabled: false, requiresOrders: false },
  { key: 'place_stop', label: 'Add Stop' },
  { key: 'close_pos',    label: 'Close Full Position', icon: '\u2715', accent: 'border-l-[rgb(var(--color-ember))]',   enabled: true,  requiresOrders: true  },
  { key: 'add_note',     label: 'Add Note',            icon: '\u270E', accent: 'border-l-[rgb(var(--color-ink-dim))]', enabled: false, requiresOrders: false },
]

export default function ActionPanel({
  projects = [],
  trades = [],
  // EF-134d: per-fill execution rows from the trades' executions table.
  // Used to group buy fills into tranches and LIFO-match them to stop
  // groups so the Move Stop accordion shows tranche-specific entries
  // instead of blended avg_entry_price.
  executions = [],
  // EF-134e: live mark price from the position header (lifted via
  // onPriceUpdate). Used to warn when BE auto-fill lands above market.
  currentPrice = null,
  // EF-134e: callback fired when the Move Stop picker has exactly one
  // group selected — value is the price (Number), or null when none /
  // multiple are selected. PositionWorkspace passes this to TradeChart
  // so the matching stop line can glow.
  onStopSelectionChange,
  symbol,
  isActive,
  onPositionRefresh,
  onStopMoved,
  // EF-114: Place Stop quick action — state lives in PositionWorkspace so the
  // header alerts strip can also drive it. ActionPanel renders the button +
  // inline price input.
  protection,
  placeStopOpen,
  placeStopPrice,
  setPlaceStopPrice,
  onPlaceStopConfirm,
  onPlaceStopCancel,
  onPlaceStop,
  workingOrdersApi,
}) {
  const localWorkingOrdersApi = useWorkingOrders()
  const {
    orders,
    rawOrders,
    fetchOrders,
    refreshForSymbol,
    getStopOrdersForSymbol,
    ready: ordersReady,
  } = workingOrdersApi || localWorkingOrdersApi
  const hasSharedWorkingOrdersApi = !!workingOrdersApi
  const { getNickname } = useAccountNicknames()
  const biometricGate = useBiometricGate()
  const { user } = useAuth()

  const openTrades = trades.filter(t =>
    (t.status === 'open' || t.status === 'partial') && t.symbol?.toUpperCase() === symbol?.toUpperCase()
  )

  // EF-195: account-number set derived from openTrades. Used as the scope
  // for the broker-shape split-bracket detector below. Memo deps are
  // [trades, symbol] (NOT [openTrades]) because openTrades is a fresh
  // filtered array on every render and would defeat the memo.
  const accountNumbers = useMemo(
    () => Array.from(new Set(openTrades.map(t => t.account_number).filter(Boolean))),
    [trades, symbol], // eslint-disable-line react-hooks/exhaustive-deps
  )

  // EF-190 + EF-195 + EF-196-PRE-BLOCKER + EF-196 + EF-199:
  // unsupported Shape C topologies still disable all guarded exits.
  // Supported Shape A/B positions can re-enable guarded quick actions only
  // when the executable-plan builders can produce complete OMS payloads. Three
  // detectors run in parallel:
  //   • EF-190 metadata path: TARGET_ENTRY / RUNNER_ENTRY / bracketGroupId
  //     on position_projects.schwab_order_ids (Erevis-tracked positions).
  //   • EF-195 broker-shape path: ≥2 same-account same-price SELL STOPs +
  //     a live bracket-parent SELL LIMIT child (Shape A) or a promoted
  //     LIMIT row (Shape B) for the same (symbol, account).
  //   • EF-196-PRE-BLOCKER unsupported-topology path: the 2026-05-27
  //     DOCN-style shape — top-level standalone SELL LIMIT (NOT
  //     promotedFromBracket) plus >1 same-account SELL STOPs — that EF-195
  //     does not admit. Until EF-195b ships Shape C admission this slice
  //     keeps the legacy quick actions disabled on that topology rather
  //     than letting them silently fire Schwab-unsafe payloads.
  // A partial metadata state (only TARGET or only RUNNER tracked) still
  // trips the guard — the legacy payloads assume a single-stop topology
  // and emit Schwab-unsafe orders either way.
  const splitBracketGuardState = useMemo(() => {
    const stopOrdersForSplitCheck = getStopOrdersForSymbol(symbol, { includeAwaitingParent: false })
    const splitDetected =
      isSplitBracketPosition(projects) ||
      hasBrokerShapeSplitBracket({ symbol, accountNumbers, rawOrders, stopOrders: stopOrdersForSplitCheck })
    const unsupported =
      isUnsupportedSplitExitTopology({ symbol, accountNumbers, rawOrders, stopOrders: stopOrdersForSplitCheck })
    return {
      supported: splitDetected && !unsupported,
      unsupported,
      any: splitDetected || unsupported,
      stopOrders: stopOrdersForSplitCheck,
    }
  }, [projects, symbol, accountNumbers, rawOrders, getStopOrdersForSymbol])

  const { omsEnabled, schwabAuthExpired, brokerConn, confirmState, showConfirmation, confirmOrder, cancelConfirmation } = useOrderGateway({
    biometricGate,
    onOrdersChanged: async () => {
      // After a successful Move Stop, sync the new price into trades.stop_loss
      // so the chart line reflects reality.
      const pendingStop = pendingStopUpdateRef.current
      console.log('[ActionPanel] onOrdersChanged fired. pendingStop:', pendingStop, 'onStopMoved:', typeof onStopMoved)

      if (pendingStop != null) {
        pendingStopUpdateRef.current = null

        if (onStopMoved) {
          console.log('[ActionPanel] Calling onStopMoved with:', pendingStop)
          onStopMoved(pendingStop)
        } else {
          console.warn('[ActionPanel] onStopMoved is NOT defined — chart stop line will not update instantly')
        }

        for (const trade of openTrades) {
          try {
            await recordStopChange(supabase, {
              tradeId: trade.id,
              stopPrice: pendingStop,
              source: 'move_stop',
              userId: user?.id,
              accountNumber: trade.account_number,
              trade,
            })
          } catch (err) {
            console.warn('[ActionPanel] Failed to record stop change:', err.message)
          }
        }
      }

      const accountNumbers = openTrades.map(t => t.account_number).filter(Boolean)
      await new Promise(r => setTimeout(r, 2000))
      const result = await refreshForSymbol(symbol, accountNumbers)
      if (result && onPositionRefresh) onPositionRefresh()
      setTimeout(async () => {
        const result2 = await refreshForSymbol(symbol, accountNumbers)
        if (result2 && onPositionRefresh) onPositionRefresh()
      }, 5000)
    },
  })

  const [moveStopOpen, setMoveStopOpen] = useState(false)
  const [newStopPrice, setNewStopPrice] = useState('')
  // EF-134: stops can exist at multiple price levels for the same symbol
  // (e.g. some accounts moved to break-even, others not). This Set holds
  // the price keys (e.g. "72.54") of groups currently selected to be
  // moved by the next "Set" click. Reset on symbol change and on close —
  // the useEffect below populates it with all current price keys whenever
  // the section opens, so the default behavior matches today's "move all".
  const [selectedStopPrices, setSelectedStopPrices] = useState(new Set())
  // EF-134c: which price groups are accordion-expanded to show per-account
  // detail rows. Independent from selectedStopPrices — expanding a group
  // shows detail, selecting/deselecting controls which stops actually move.
  const [expandedGroups, setExpandedGroups] = useState(new Set())
  // EF-134e: tracks whether the price input was last filled by the BE
  // button. When true, switching stop groups recomputes BE for the new
  // selection. Manual typing flips this off so user input isn't clobbered.
  const [beActive, setBeActive] = useState(false)
  const [notification, setNotification] = useState(null)
  // EF-190: shared guard helper. Returns true when the action is blocked and a
  // warning notification has been raised; callers must early-return on `true`.
  // Used by both the click router AND the three handler entry points, so a
  // direct call site (e.g. the Move Stop Set button at the bottom of the
  // accordion) cannot bypass the guard if `projects` updates into a
  // split-bracket state while the accordion is already open.
  const blockSplitBracketQuickAction = (actionKey) => {
    const blocked =
      splitBracketGuardState.unsupported &&
      !(actionKey === 'take_profit' && takeProfitScaleOutPlan.supported)
    if (!blocked) return false
    setNotification({ type: 'warning', message: UNSUPPORTED_SPLIT_GUARD_MESSAGE })
    return true
  }
  // EF-134e-fix2: inline warning shown directly below the price input when
  // the user clicks Set without selecting a stop group. Separate from the
  // top-level notification so it renders next to the trigger, not in the
  // far-away column header.
  const [moveStopWarning, setMoveStopWarning] = useState(null)
  // Gates stop-dependent action buttons until the initial loadOrders chain
  // completes. Without this, ordersReady flips mid-fetch and buttons flash
  // enabled → disabled → enabled.
  const [actionsReady, setActionsReady] = useState(false)
  // After a successful Move Stop, we sync the new price into trades.stop_loss
  // so the chart line reflects reality. Cleared after the onOrdersChanged
  // callback runs.
  const pendingStopUpdateRef = useRef(null)

  const splitMoveStopPlan = useMemo(() => buildMoveStopReplacePlan({
    symbol,
    openTrades,
    stopOrders: splitBracketGuardState.stopOrders,
    selectedStopPrices: null,
    newStopPrice: 1,
    getAccountName: (accountNumber) => getNickname(accountNumber),
  }), [symbol, openTrades, splitBracketGuardState.stopOrders, getNickname])

  const splitCloseFullPlan = useMemo(() => buildCloseFullPositionPlan({
    symbol,
    openTrades,
    rawOrders,
    stopOrders: splitBracketGuardState.stopOrders,
    getAccountName: (accountNumber) => getNickname(accountNumber),
  }), [symbol, openTrades, rawOrders, splitBracketGuardState.stopOrders, getNickname])

  const takeProfitScaleOutPlan = useMemo(() => buildTakeProfitScaleOutPlan({
    symbol,
    openTrades,
    rawOrders,
    stopOrders: splitBracketGuardState.stopOrders,
    getAccountName: (accountNumber) => getNickname(accountNumber),
  }), [symbol, openTrades, rawOrders, splitBracketGuardState.stopOrders, getNickname])

  const splitBracketBannerMessage = useMemo(() => {
    if (splitBracketGuardState.unsupported) return UNSUPPORTED_SPLIT_GUARD_MESSAGE
    if (!splitBracketGuardState.supported) return null
    if (
      !splitMoveStopPlan.supported ||
      !splitCloseFullPlan.supported ||
      !takeProfitScaleOutPlan.supported
    ) {
      return SPLIT_BRACKET_PLAN_BLOCKED_MESSAGE
    }
    return null
  }, [
    splitBracketGuardState.unsupported,
    splitBracketGuardState.supported,
    splitMoveStopPlan.supported,
    splitCloseFullPlan.supported,
    takeProfitScaleOutPlan.supported,
    takeProfitScaleOutPlan.reason,
  ])

  useEffect(() => {
    if (!notification) return
    const timer = setTimeout(() => setNotification(null), 10000)
    return () => clearTimeout(timer)
  }, [notification])

  // EF-134e-fix2: shorter auto-clear for the inline Move Stop warning —
  // 5s feels right for a "you forgot to do X" hint vs. 10s for a real
  // error notification at the top of the column.
  useEffect(() => {
    if (!moveStopWarning) return
    const timer = setTimeout(() => setMoveStopWarning(null), 5000)
    return () => clearTimeout(timer)
  }, [moveStopWarning])

  // Derive account numbers from trades (stable string key for dependency)
  const tradeAccountNumbers = trades.map(t => t.account_number).filter(Boolean)
  const tradeAccountKey = tradeAccountNumbers.join(',')

  // Initial load — targeted refresh first, full fetch as automatic fallback.
  // Sets actionsReady=true once the chain finishes so stop-dependent buttons
  // flip from "Loading orders..." straight to enabled with no flash in between.
  useEffect(() => {
    if (!isActive || !symbol) {
      setActionsReady(false)
      return
    }
    if (hasSharedWorkingOrdersApi) return
    let cancelled = false
    setActionsReady(false)

    const loadOrders = async () => {
      try {
        let hasStops = false

        if (tradeAccountNumbers.length > 0) {
          // Use the orders RETURNED by refreshForSymbol to detect stops
          // instead of reading getStopOrdersForSymbol() — that's a
          // useCallback over the orders state, and the closure here was
          // captured before refreshForSymbol's setOrders propagated, so
          // it always saw an empty array and ALWAYS triggered the slow
          // fetchOrders fallback. Reading the returned data sidesteps
          // React's async re-render entirely.
          const result = await refreshForSymbol(symbol, tradeAccountNumbers)
          if (cancelled) return

          if (result?.orders) {
            const upperSymbol = symbol.toUpperCase()
            hasStops = result.orders.some(o => {
              const sym = o.symbol || ''
              return sym === upperSymbol && (o.orderType === 'STOP' || o.orderType === 'STOP_LIMIT' || o.stopPrice)
            })
          }
        }

        if (!hasStops && !cancelled) {
          console.warn(`[ActionPanel] No stops for ${symbol} after targeted refresh — trying full fetch`)
          await fetchOrders()
        }
      } catch (err) {
        console.error('[ActionPanel] loadOrders error:', err)
      } finally {
        // ALWAYS resolve loading state. cancelled only prevents stale
        // data from being USED — it must not gate the UI from becoming
        // interactive. If the effect re-fires, the top resets
        // actionsReady=false anyway.
        setActionsReady(true)
      }
    }

    loadOrders()
    return () => { cancelled = true }
  }, [isActive, symbol, tradeAccountKey, hasSharedWorkingOrdersApi]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isActive || !symbol || !hasSharedWorkingOrdersApi) return
    setActionsReady(ordersReady !== false)
  }, [isActive, symbol, hasSharedWorkingOrdersApi, ordersReady])

  // EF-134: when the Move Stop section opens, populate selectedStopPrices
  // with all current price keys so the default behavior is "move all" (no
  // regression for the common single-price case). On close or symbol
  // change, clear the set — the next open will re-populate from fresh
  // stop data. getStopOrdersForSymbol is intentionally excluded from deps
  // so user selections aren't clobbered when the orders state refreshes
  // mid-session (same eslint-disable pattern as the loadOrders effect).
  useEffect(() => {
    // EF-134e-fix: open with NOTHING selected. Move Stop is a deliberate
    // action — the user must explicitly click a group (or Select All)
    // before Set is meaningful. Prevents accidental "move all" and matches
    // the rest of the picker's radio-style intent.
    setSelectedStopPrices(new Set())
    setExpandedGroups(new Set())
    setBeActive(false)
    // EF-134e-fix2: stale "select a stop first" warning shouldn't survive
    // a close/reopen or a symbol change.
    setMoveStopWarning(null)
  }, [symbol, moveStopOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  // EF-134d: group buy executions into tranches and LIFO-match each tranche
  // to a stop price group. The blended `trades.avg_entry_price` mixes every
  // historical add into one number, which is wrong when different stop levels
  // protect different tranches (e.g. GLW Mar tranche at $135.4x → stop $135.42,
  // Apr tranche at $162.00 → stop $140.98). Tranches are detected as fill
  // bursts within a 5-minute window. LIFO match: lowest stop covers the oldest
  // tranche, highest stop covers the newest — Schwab sells newest shares first
  // on stop trigger, so the newest tranche is what's at risk under the highest
  // stop. Returns Map<priceKey, executions[]>.
  const trancheMap = useMemo(() => {
    if (!executions?.length) return new Map()

    const upperSymbol = symbol?.toUpperCase()
    const buys = executions.filter(e =>
      e.side === 'buy' && e.symbol?.toUpperCase() === upperSymbol
    )
    if (buys.length === 0) return new Map()

    const stopOrders = getStopOrdersForSymbol(symbol, { includeAwaitingParent: false })
    const tranches = groupBuyExecutionsIntoTranches({ executions: buys, symbol })
    return matchStopGroupsToBuyTranches({ buyTranches: tranches, stopOrders })
  }, [executions, symbol, getStopOrdersForSymbol])

  // EF-134b / EF-134d: Break even = highest entry price across the relevant
  // accounts so no account loses money at the BE stop. EF-134d makes it
  // tranche-aware: when exactly one stop group is selected, BE uses that
  // tranche's per-account weighted avg (max across accounts). When multiple
  // groups are selected (or tranche data is missing), falls back to EF-134b's
  // max-of-blended-avg. Returns null when no usable data — caller disables
  // the BE button.
  const computeBreakEven = () => {
    const selectedKeys = [...selectedStopPrices]
    if (selectedKeys.length === 1) {
      const trancheExecs = trancheMap.get(selectedKeys[0])
      if (trancheExecs?.length) {
        const accountEntries = {}
        for (const exec of trancheExecs) {
          const acct = exec.account_number
          if (!accountEntries[acct]) accountEntries[acct] = { totalCost: 0, totalQty: 0 }
          accountEntries[acct].totalCost += Number(exec.price) * Number(exec.quantity)
          accountEntries[acct].totalQty += Number(exec.quantity)
        }
        let maxEntry = null
        for (const acct of Object.values(accountEntries)) {
          if (acct.totalQty > 0) {
            const avgEntry = acct.totalCost / acct.totalQty
            if (maxEntry === null || avgEntry > maxEntry) maxEntry = avgEntry
          }
        }
        if (maxEntry !== null) return maxEntry
      }
    }

    let maxEntry = null
    for (const trade of openTrades) {
      const qty = Number(trade.quantity_open || trade.quantity_total) || 0
      const entry = Number(trade.avg_entry_price) || 0
      if (qty > 0 && entry > 0) {
        if (maxEntry === null || entry > maxEntry) {
          maxEntry = entry
        }
      }
    }
    return maxEntry
  }

  // EF-134c-style: radio-style selection AND auto-expand in one action.
  // Clicking a group (anywhere on the row) selects ONLY that group AND
  // expands its accordion AND collapses every other accordion. One click
  // = select + see the per-account detail. The "at least one selected"
  // invariant from EF-134 is preserved — clicking the already-only-
  // selected group is a no-op on selection but still ensures its
  // accordion is the open one.
  const toggleStopGroup = (priceKey) => {
    setSelectedStopPrices(prev => {
      if (prev.has(priceKey) && prev.size === 1) return prev
      return new Set([priceKey])
    })
    setExpandedGroups(new Set([priceKey]))
    // EF-134e-fix2: any selection action satisfies the "select a stop first"
    // condition, so the inline warning should clear immediately rather than
    // waiting for its 5s auto-dismiss.
    setMoveStopWarning(null)
  }

  // EF-134e: auto-update price field when selection changes. If BE mode is
  // active (last fill was from the BE button), recalculate for the new
  // selection so e.g. switching from the $140.98 group to the $135.42 group
  // updates the input from $162.00 → $135.43. If the user typed manually,
  // clear the field instead — a price computed for the prior group is
  // almost certainly wrong for the new one.
  useEffect(() => {
    if (!moveStopOpen) return
    if (beActive) {
      const be = computeBreakEven()
      if (be !== null) setNewStopPrice(be.toFixed(2))
    } else if (newStopPrice) {
      setNewStopPrice('')
    }
  }, [selectedStopPrices]) // eslint-disable-line react-hooks/exhaustive-deps

  // EF-134e-fix: notify parent of active stop price(s) for chart glow.
  // Now sends an array of Numbers: single selection → [price], Select All
  // → [price1, price2, ...], nothing selected or picker closed → null.
  // TradeChart iterates and glows every matching line.
  useEffect(() => {
    if (!onStopSelectionChange) return
    if (moveStopOpen && selectedStopPrices.size > 0) {
      onStopSelectionChange([...selectedStopPrices].map(Number))
    } else {
      onStopSelectionChange(null)
    }
  }, [selectedStopPrices, moveStopOpen, onStopSelectionChange])

  // EF-134e-fix2: ESC key closes whichever quick action is currently open.
  // The Move Stop input already handles Enter for submit. The Place Stop
  // input has its own ESC handler when focused. This document-level
  // listener catches ESC when focus is OUTSIDE the inputs — e.g. the user
  // clicks a stop group, then hits ESC to dismiss the whole section.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (moveStopOpen) {
          setMoveStopOpen(false)
          e.stopPropagation()
        } else if (placeStopOpen && onPlaceStopCancel) {
          onPlaceStopCancel()
          e.stopPropagation()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [moveStopOpen, placeStopOpen, onPlaceStopCancel])

  // ── Move Stop Handler ──────────────────────────────────────
  const handleMoveStop = () => {
    // EF-190: split-bracket guard — the Set button + Enter-key handler at the
    // bottom of the Move Stop accordion call handleMoveStop directly without
    // going through handleAction, so the guard must live at the handler entry
    // point. Without this, an accordion already open when projects updates
    // into a split-bracket state could still dispatch the legacy payload.
    if (blockSplitBracketQuickAction('move_stop')) return

    const price = parseFloat(newStopPrice)
    if (!price || price <= 0) return

    // EF-134e-fix: require an explicit stop group selection before moving.
    // Picker now opens with nothing selected — clicking Set on an empty
    // selection would have built zero gateway orders and silently no-op'd.
    if (selectedStopPrices.size === 0) {
      // EF-134e-fix2: inline warning rendered next to the Set button
      // instead of the top-of-column notification slot.
      setMoveStopWarning('Select a stop group first.')
      return
    }

    // Only operate on WORKING standalone stops — NOT bracket children
    // waiting for an entry trigger. Those protect future shares, not
    // current ones.
    const stopOrders = getStopOrdersForSymbol(symbol, { includeAwaitingParent: false })
    if (stopOrders.length === 0) {
      const allStops = getStopOrdersForSymbol(symbol, { includeAwaitingParent: true })
      const bracketOnly = allStops.filter(s => s.isBracket)
      if (bracketOnly.length > 0) {
        setNotification({
          type: 'error',
          message: `Found ${bracketOnly.length} bracket stop(s) for ${symbol} but they are waiting for entry triggers to fill. These protect future shares, not your current position. Place a standalone stop in TOS to protect your active shares.`,
        })
      } else {
        setNotification({
          type: 'error',
          message: `No active stop orders found for ${symbol}. Place a stop in TOS first, then use Move Stop to adjust it.`,
        })
      }
      return
    }

    // EF-134: when stops exist at multiple price levels, only move the
    // groups the user explicitly selected via the picker. Single-price
    // case skips this entirely and moves all stops (zero regression).
    const stopsByPrice = {}
    for (const stop of stopOrders) {
      const numPrice = Number(stop.stopPrice)
      if (!numPrice || isNaN(numPrice)) continue
      const priceKey = numPrice.toFixed(2)
      if (!stopsByPrice[priceKey]) stopsByPrice[priceKey] = []
      stopsByPrice[priceKey].push(stop)
    }
    const priceGroups = Object.entries(stopsByPrice)
    const stopsToMove = priceGroups.length > 1
      ? stopOrders.filter(s => selectedStopPrices.has(Number(s.stopPrice).toFixed(2)))
      : stopOrders

    const plan = buildMoveStopReplacePlan({
      symbol,
      openTrades,
      stopOrders: stopsToMove,
      selectedStopPrices: null,
      newStopPrice: price,
      allowTradeQuantityFallback: !splitBracketGuardState.supported,
      getAccountName: (accountNumber) => getNickname(accountNumber),
    })

    if (!plan.supported) {
      setNotification({
        type: 'error',
        message: plan.reason || `Cannot build a safe Move Stop plan for ${symbol}.`,
      })
      return
    }

    const gatewayOrders = plan.gatewayOrders.map(order => ({
      ...order,
      requiresBrokerConfirmation: splitBracketGuardState.supported,
    }))

    pendingStopUpdateRef.current = price
    showConfirmation('REPLACE', symbol, plan.orderSummaries, gatewayOrders)
    setMoveStopOpen(false)
    setNewStopPrice('')
  }

  // ── Take 1/3 Profit Handler (cancel targets, adjust stops, then market sell) ──
  const handleTakeProfit = () => {
    // EF-190: split-bracket guard at the handler entry point (defense in depth
    // — the router-level guard already covers the click path through
    // handleAction, but a future direct call site would otherwise slip past).
    if (blockSplitBracketQuickAction('take_profit')) return

    if (openTrades.length === 0) {
      setNotification({ type: 'error', message: `No open trades found for ${symbol}.` })
      return
    }

    if (!takeProfitScaleOutPlan.supported) {
      setNotification({
        type: 'warning',
        message: takeProfitScaleOutPlan.reason || 'Cannot build a safe Take 1/3 Profit plan.',
      })
      return
    }

    const batches = takeProfitScaleOutPlan.batches.map(batch => batch.action === 'REPLACE' || batch.action === 'CANCEL'
      ? {
          ...batch,
          orders: batch.orders.map(order => ({
            ...order,
            requiresBrokerConfirmation: true,
          })),
        }
      : batch)

    showConfirmation('TAKE_PROFIT', symbol, takeProfitScaleOutPlan.orderSummaries, null, batches)
  }

  // ── Flatten at Market Handler (two-leg: cancel stops, then market sell full) ──
  const handleFlatten = () => {
    // EF-190: split-bracket guard at the handler entry point (defense in depth).
    if (blockSplitBracketQuickAction('close_pos')) return

    if (openTrades.length === 0) {
      setNotification({ type: 'error', message: `No open trades found for ${symbol}.` })
      return
    }

    const stopOrders = getStopOrdersForSymbol(symbol, { includeAwaitingParent: false })

    if (splitBracketGuardState.supported) {
      const plan = buildCloseFullPositionPlan({
        symbol,
        openTrades,
        rawOrders,
        stopOrders,
        getAccountName: (accountNumber) => getNickname(accountNumber),
      })

      if (!plan.supported) {
        setNotification({
          type: 'error',
          message: plan.reason || `Cannot build a safe Close Full Position plan for ${symbol}.`,
        })
        return
      }

      const batches = plan.batches.map(batch => batch.action === 'CANCEL'
        ? {
            ...batch,
            orders: batch.orders.map(order => ({
              ...order,
              requiresBrokerConfirmation: true,
            })),
          }
        : batch)
      showConfirmation('FLATTEN', symbol, plan.orderSummaries, null, batches)
      return
    }

    const orderSummaries = []
    const cancelOrders = []
    const sellOrders = []

    for (const trade of openTrades) {
      const qty = trade.quantity_open || trade.quantity_total
      if (!qty || qty <= 0) continue

      const accountStops = stopOrders.filter(s => s.accountNumber === trade.account_number)

      for (const stop of accountStops) {
        cancelOrders.push({
          account_number: trade.account_number,
          symbol: symbol.toUpperCase(),
          action: 'CANCEL',
          existingOrderId: stop.orderId,
        })
      }

      sellOrders.push({
        account_number: trade.account_number,
        symbol: symbol.toUpperCase(),
        side: 'SELL',
        quantity: qty,
        orderType: 'MARKET',
        duration: 'DAY',
        session: 'NORMAL',
        assetType: 'EQUITY',
      })

      orderSummaries.push({
        accountName: getNickname(trade.account_number) || `****${trade.account_number.slice(-4)}`,
        accountNumber: trade.account_number,
        quantity: qty,
        side: 'SELL',
        orderType: 'MARKET',
        stopsToCancel: accountStops.length,
      })
    }

    if (sellOrders.length === 0) {
      setNotification({ type: 'error', message: `No sellable quantity found for ${symbol}.` })
      return
    }

    const batches = []
    if (cancelOrders.length > 0) batches.push({ action: 'CANCEL', orders: cancelOrders })
    batches.push({ action: 'PLACE', orders: sellOrders })

    showConfirmation('FLATTEN', symbol, orderSummaries, null, batches)
  }

  // ── Button click router ────────────────────────────────────
  const handleAction = (key) => {
    // EF-190: router-level split-bracket guard — uses the same helper so any
    // UI surface (this top-level button or any future one) is covered.
    // Handler-level guards inside handleMoveStop / handleTakeProfit /
    // handleFlatten still fire for direct call sites that bypass this router.
    if (GUARDED_LEGACY_QUICK_ACTION_KEYS.has(key) && blockSplitBracketQuickAction(key)) return

    if (key === 'move_stop') {
      setMoveStopOpen(prev => !prev)
      // EF-134e-fix2: opening Move Stop closes Place Stop. Mutually exclusive
      // expansion keeps the column visually focused on one quick action at a time.
      if (placeStopOpen && onPlaceStopCancel) onPlaceStopCancel()
    }
    else if (key === 'take_profit') handleTakeProfit()
    else if (key === 'close_pos') handleFlatten()
  }

  // Stop-dependent actions wait for actionsReady so they don't flash; other
  // actions just need OMS + an active position.
  const canPlaceStop = Boolean(isActive && onPlaceStop && protection?.unprotected > 0)
  const canActBase = isActive && omsEnabled
  const canActWithOrders = canActBase && actionsReady

  return (
    <div
      className="space-y-6 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
      data-command-target="position.actions.focus"
      tabIndex={-1}
    >
      {/* Notification banner */}
      {notification && (
        <div className={`font-mono text-[11px] px-3 py-2.5 rounded-lg border ${
          notification.type === 'error'
            ? 'bg-ember/10 border-ember/30 text-ember'
            : notification.type === 'warning'
              ? 'bg-[#c4953a]/10 border-[#c4953a]/30 text-[#c4953a]'
              : 'bg-forest/10 border-forest/30 text-forest'
        }`}>
          <div className="flex items-start justify-between gap-2">
            <span className="leading-snug">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="text-inkMuted hover:text-ink shrink-0 mt-0.5"
            >{'\u00d7'}</button>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <div className="flex items-center justify-end px-1 pt-1 mb-3">
          <div className="font-mono text-[10px] tracking-wider">
            {schwabAuthExpired ? (
              <span className="text-ember/80 inline-flex items-center gap-1.5 uppercase tracking-widest"><span className="w-1.5 h-1.5 rounded-full bg-ember animate-pulse" />Schwab auth expired</span>
            ) : omsEnabled ? (
              !actionsReady
                ? <span className="text-inkMuted inline-flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" />Connecting to OMS…</span>
                : <span style={{ color: 'rgb(var(--color-forest))' }}>{'\u25CF'} OMS connected</span>
            ) : (
              <span className="text-inkMuted">{'\u25CB'} OMS disabled</span>
            )}
          </div>
        </div>
        {/* EF-190+: inline warning banner only appears when a split-derived
            topology is still blocking at least one guarded quick action. */}
        {splitBracketBannerMessage && (
          <div
            className="font-mono text-[11px] px-3 py-2.5 rounded-lg border bg-[#c4953a]/10 border-[#c4953a]/30 text-[#c4953a] mb-3 leading-snug"
            role="status"
            data-testid="split-bracket-disable-banner"
          >
            {splitBracketBannerMessage}
          </div>
        )}
        <div className="grid grid-cols-2 gap-1.5" data-testid="position-quick-action-grid">
          {ACTIONS.map(action => {
            if (action.key === 'place_stop') return (
            <div key="place_stop" className="min-w-0">
              {/* EF-159: button restyled to match the ACTIONS-card pattern below
                  (left accent border, font-mono text-xs, same padding). The
                  ember accent stripe matches the EF-134c-style "STOP LOSS"
                  red used in the Move Stop picker — same risk semantic. */}
              <button
                disabled={!canPlaceStop}
                title={canPlaceStop ? '' : !isActive ? 'Position must be active' : 'No unprotected shares'}
                onClick={() => { if (moveStopOpen) setMoveStopOpen(false); onPlaceStop() }}
                className={`w-full text-left font-mono text-xs px-3 py-2.5 rounded-lg border border-border bg-canvas
                  border-l-[3px] border-l-[rgb(var(--color-ember))]
                  ${canPlaceStop ? 'opacity-100 cursor-pointer hover:bg-canvas2' : 'opacity-40 cursor-not-allowed'} transition-colors`}
              >
                <span className="text-ink">⊘ Add Stop</span>
              </button>
              {canPlaceStop && placeStopOpen && (
                <div className="mt-1.5">
                  {/* EF-159: gap summary moved ABOVE the input (context before action)
                      and labeled "unprotected" for clarity. */}
                  {/* EF-159b: per-account rows mirror the Move Stop accordion's
                      detail-row pattern — nickname left (w-20 truncate), gap on the
                      right in ember to convey urgency. The previous inline ` · `
                      separator wrapped into an unreadable blob with 4+ accounts. */}
                  {protection?.accountGaps?.length > 0 && (
                    <div className="space-y-0.5 px-3 mb-1.5">
                      {protection.accountGaps.map(a => (
                        <div
                          key={a.accountNumber}
                          className="flex items-center justify-between py-0.5"
                        >
                          <span className="font-mono text-[9px] text-inkMuted w-20 truncate">
                            {getNickname(a.accountNumber) || `****${a.accountNumber.slice(-4)}`}
                          </span>
                          <span className="font-mono text-[9px] text-ember">
                            {a.gap} sh unprotected
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* EF-159: input row mirrors the Move Stop input pattern —
                      $ prefix, flex-1 input, single Set button. Cancel button
                      removed since ESC already handles it (line ~382 ESC handler)
                      and Move Stop has no Cancel either. */}
                  <div className="flex items-center gap-2 px-3">
                    <span className="font-mono text-[10px] text-inkMuted">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      value={placeStopPrice}
                      onChange={(e) => setPlaceStopPrice(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onPlaceStopConfirm()
                        if (e.key === 'Escape') onPlaceStopCancel()
                      }}
                      placeholder="Stop price"
                      autoFocus
                      style={{ fontSize: 16 }}
                      className="min-w-0 flex-1 bg-canvas2 border border-border rounded px-2 py-1.5 font-mono text-xs text-ink placeholder:text-inkMuted focus:outline-none focus:border-ink/40"
                    />
                    <button
                      onClick={onPlaceStopConfirm}
                      disabled={!placeStopPrice || parseFloat(placeStopPrice) <= 0}
                      className="font-mono text-[10px] font-semibold px-3 py-1.5 rounded border border-ember text-ember hover:bg-ember/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Set
                    </button>
                  </div>
                </div>
              )}
            </div>
            )

            // EF-190: legacy quick action, hidden behind split-bracket guard
            // until proper support ships. The three guarded keys are disabled
            // (in addition to whatever else gated them) and get a tooltip
            // explaining why; other action keys are unaffected.
            const isLegacyGuarded = GUARDED_LEGACY_QUICK_ACTION_KEYS.has(action.key)
            const planBlocked =
              action.key === 'move_stop'
                ? !splitMoveStopPlan.supported
                : action.key === 'close_pos'
                  ? !splitCloseFullPlan.supported
                  : action.key === 'take_profit'
                    ? !takeProfitScaleOutPlan.supported
                    : true
            const blockedBySplit =
              isLegacyGuarded &&
              (
                (splitBracketGuardState.unsupported && !(action.key === 'take_profit' && !planBlocked)) ||
                (splitBracketGuardState.supported && planBlocked)
              )
            const isEnabled =
              action.enabled &&
              (action.requiresOrders ? canActWithOrders : canActBase) &&
              !blockedBySplit &&
              !(action.key === 'take_profit' && planBlocked)
            const title = action.key === 'take_profit' && planBlocked
              ? takeProfitScaleOutPlan.reason
              : blockedBySplit
              ? (splitBracketBannerMessage || UNSUPPORTED_SPLIT_GUARD_MESSAGE)
              : !isActive ? 'Position must be active'
              : !omsEnabled ? 'Enable OMS in Settings'
              : !action.enabled ? 'Coming soon'
              : ''
            return (
              <div
                key={action.key}
                {...(action.key === 'move_stop' ? {
                  'data-command-id': 'position.move_stop.focus',
                  'data-command-target': 'position.move_stop.focus',
                  tabIndex: -1,
                } : {})}
                className={action.key === 'move_stop' ? 'rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-[#c4953a]/60' : undefined}
              >
                <button
                  disabled={!isEnabled}
                  onClick={() => handleAction(action.key)}
                  title={title}
                  className={`w-full text-left font-mono text-xs px-3 py-2.5 rounded-lg border border-border bg-canvas
                    border-l-[3px] ${action.accent}
                    ${isEnabled ? 'opacity-100 cursor-pointer hover:bg-canvas2 transition-colors' : 'opacity-40 cursor-not-allowed'}`}
                >
                  <span className="text-ink">{action.icon} {action.label}</span>
                </button>

                {action.key === 'move_stop' && moveStopOpen && canActWithOrders && (() => {
                  // EF-134: compute price groups for the picker. Mirrors the
                  // logic in handleMoveStop so the rendered choices and the
                  // filter that consumes them stay in sync per render.
                  const moveStopOrdersForUI = getStopOrdersForSymbol(symbol, { includeAwaitingParent: false })
                  const stopsByPriceUI = {}
                  for (const stop of moveStopOrdersForUI) {
                    const numPrice = Number(stop.stopPrice)
                    if (!numPrice || isNaN(numPrice)) continue
                    const priceKey = numPrice.toFixed(2)
                    if (!stopsByPriceUI[priceKey]) stopsByPriceUI[priceKey] = []
                    stopsByPriceUI[priceKey].push(stop)
                  }
                  const priceGroupsUI = Object.entries(stopsByPriceUI)
                  const breakEven = computeBreakEven()
                  const beDisabled = breakEven === null

                  return (
                    <div className="mt-1.5">
                      {priceGroupsUI.length > 0 && (() => {
                        // EF-134c-style: visual all-selected indicator. Click selects
                        // every group AND collapses every accordion (one click resets
                        // to the default "all selected, all collapsed" state).
                        const allSelected = priceGroupsUI.every(([k]) => selectedStopPrices.has(k))
                        const selectAll = () => {
                          // EF-134e-fix2: Select All is now a true toggle. When
                          // everything is already selected, clicking it deselects
                          // and collapses all accordions (returns the picker to
                          // the empty initial state). Otherwise, selects + expands.
                          if (allSelected) {
                            setSelectedStopPrices(new Set())
                            setExpandedGroups(new Set())
                          } else {
                            setSelectedStopPrices(new Set(priceGroupsUI.map(([k]) => k)))
                            setExpandedGroups(new Set(priceGroupsUI.map(([k]) => k)))
                          }
                          setMoveStopWarning(null)
                        }
                        return (
                          <>
                            <div className="px-3">
                              {/* EF-134e-fix: Select All only when there's more than one
                                  group to choose from. Single-stop positions skip
                                  straight to the (single) group row. */}
                              {priceGroupsUI.length > 1 && (
                                <>
                                  <button
                                    type="button"
                                    onClick={selectAll}
                                    className="w-full flex items-center gap-1.5 px-3 py-1.5 rounded hover:bg-white/[0.03] cursor-pointer transition-colors"
                                  >
                                    <div className={`w-3.5 h-3.5 rounded-full border flex-shrink-0 flex items-center justify-center transition-colors ${
                                      allSelected
                                        ? 'border-[#c4953a] bg-[#c4953a]/20'
                                        : 'border-inkMuted/40 hover:border-inkMuted/60'
                                    }`}>
                                      {allSelected && <div className="w-1.5 h-1.5 rounded-full bg-[#c4953a]" />}
                                    </div>
                                    <span className="font-mono text-[9px] text-inkMuted/60 uppercase tracking-wider">Select All</span>
                                  </button>
                                  <div className="border-t border-white/[0.06] my-1" />
                                </>
                              )}
                              {priceGroupsUI.map(([priceKey, stops]) => {
                                const selected = selectedStopPrices.has(priceKey)
                                const expanded = expandedGroups.has(priceKey)
                                const acctCount = new Set(stops.map(s => s.accountNumber)).size

                                // Per-account detail rows for the accordion. Sort by
                                // risk ascending (largest loss first). Defensive null
                                // trade — should not happen but renders "—" if it does.
                                // EF-134d: prefer tranche-specific entry (this stop group's
                                // matched fills) over the blended trades.avg_entry_price.
                                const trancheExecs = trancheMap.get(priceKey)
                                const detailRows = stops.map(stop => {
                                  let entryPrice = 0
                                  if (trancheExecs?.length) {
                                    const accountExecs = trancheExecs.filter(e => e.account_number === stop.accountNumber)
                                    if (accountExecs.length > 0) {
                                      const totalCost = accountExecs.reduce((sum, e) => sum + Number(e.price) * Number(e.quantity), 0)
                                      const totalQty = accountExecs.reduce((sum, e) => sum + Number(e.quantity), 0)
                                      entryPrice = totalQty > 0 ? totalCost / totalQty : 0
                                    }
                                  }
                                  if (!entryPrice) {
                                    const trade = openTrades.find(t => t.account_number === stop.accountNumber)
                                    entryPrice = Number(trade?.avg_entry_price) || 0
                                  }
                                  const stopQty = Number(stop.quantity) || 0
                                  const hasTradeData = entryPrice > 0
                                  const risk = hasTradeData ? stopQty * (Number(priceKey) - entryPrice) : 0
                                  const nickname = getNickname(stop.accountNumber) || `****${stop.accountNumber.slice(-4)}`
                                  return { stopKey: stop.orderId || `${stop.accountNumber}|${stopQty}`, nickname, stopQty, entryPrice, hasTradeData, risk }
                                }).sort((a, b) => a.risk - b.risk)

                                return (
                                  <div key={priceKey}>
                                    {/* EF-134e: row layout mirrors WorkingOrdersPanel
                                        Account Breakdown — circle LEFT, then chevron,
                                        then red STOP LOSS label, then price, then
                                        right-aligned account count. */}
                                    <button
                                      type="button"
                                      onClick={() => toggleStopGroup(priceKey)}
                                      className="w-full flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-white/[0.03] cursor-pointer transition-colors"
                                    >
                                      <div className={`w-3.5 h-3.5 rounded-full border flex-shrink-0 flex items-center justify-center transition-colors ${
                                        selected
                                          ? 'border-[#c4953a] bg-[#c4953a]/20'
                                          : 'border-inkMuted/40 hover:border-inkMuted/60'
                                      }`}>
                                        {selected && <div className="w-1.5 h-1.5 rounded-full bg-[#c4953a]" />}
                                      </div>
                                      <span className="text-[9px] text-inkMuted/60">{expanded ? '▾' : '▸'}</span>
                                      <span className="font-mono text-[9px] font-semibold text-ember">STOP LOSS</span>
                                      <span className="font-mono text-[10px] text-ink">${priceKey}</span>
                                      <span className="font-mono text-[9px] text-inkMuted/60 ml-auto">
                                        {acctCount} account{acctCount !== 1 ? 's' : ''}
                                      </span>
                                    </button>
                                    {expanded && (
                                      <div className="mt-1 space-y-0.5">
                                        {detailRows.map(({ stopKey, nickname, stopQty, entryPrice, hasTradeData, risk }) => {
                                          // EF-134e: round risk to whole dollars BEFORE choosing the
                                          // color. Sub-dollar risk (e.g. -$0.04 from per-share fractions)
                                          // would otherwise render as red "-$0", which falsely looks
                                          // alarming for what's effectively break-even.
                                          const roundedRisk = Math.round(risk)
                                          const riskColor = !hasTradeData ? 'text-inkMuted/40'
                                            : roundedRisk < 0 ? 'text-ember'
                                            : roundedRisk > 0 ? 'text-forest'
                                            : 'text-inkMuted/40'
                                          const riskLabel = !hasTradeData ? '—'
                                            : roundedRisk < 0 ? `-$${Math.abs(roundedRisk)}`
                                            : roundedRisk > 0 ? `+$${roundedRisk}`
                                            : '$0'
                                          return (
                                            <div
                                              key={stopKey}
                                              className="flex items-center justify-between pl-5 pr-3 py-1 rounded hover:bg-white/[0.02] transition-colors"
                                            >
                                              <div className="flex items-center gap-2">
                                                <span className="font-mono text-[9px] text-inkMuted w-20 truncate">{nickname}</span>
                                                <span className="font-mono text-[9px] text-inkMuted">{stopQty} sh</span>
                                                <span className="font-mono text-[9px] text-inkMuted/50">
                                                  {hasTradeData ? `$${entryPrice.toFixed(2)}` : '—'}
                                                </span>
                                              </div>
                                              <span className={`font-mono text-[9px] font-semibold ${riskColor}`}>
                                                {riskLabel}
                                              </span>
                                            </div>
                                          )
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                            <div className="border-t border-white/[0.06] my-1.5" />
                          </>
                        )
                      })()}
                      <div className="flex items-center gap-2 px-3">
                        <span className="font-mono text-[10px] text-inkMuted">$</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          placeholder="New stop price"
                          value={newStopPrice}
                          // EF-134e: manual input flips beActive off so the
                          // selection-change effect doesn't clobber typed values.
                          onChange={e => { setNewStopPrice(e.target.value); setBeActive(false) }}
                          onKeyDown={e => e.key === 'Enter' && handleMoveStop()}
                          autoFocus
                          style={{ fontSize: 16 }}
                          className="flex-1 bg-canvas2 border border-border rounded px-2 py-1.5 font-mono text-xs text-ink placeholder:text-inkMuted focus:outline-none focus:border-ink/40"
                        />
                        <button
                          type="button"
                          // EF-134e: setting beActive=true here arms the selection-change
                          // effect to re-fill the field if the user switches stop groups.
                          onClick={() => { if (breakEven !== null) { setNewStopPrice(breakEven.toFixed(2)); setBeActive(true) } }}
                          disabled={beDisabled}
                          title="Move to break even (avg entry)"
                          // EF-134e-fix: solid border + tinted background while BE is the
                          // active source of the input price; reverts to subtle outline
                          // the moment the user types a manual value.
                          className={`font-mono text-[10px] font-semibold px-2 py-1.5 rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            beActive
                              ? 'border-forest bg-forest/20 text-forest shadow-[0_0_6px_rgba(106,170,94,0.3)]'
                              : 'border-forest/20 text-forest/40 hover:border-forest/40 hover:text-forest hover:bg-forest/10'
                          }`}
                        >
                          BE
                        </button>
                        <button
                          onClick={handleMoveStop}
                          disabled={!newStopPrice}
                          className="font-mono text-[10px] font-semibold px-3 py-1.5 rounded border border-[#c4953a] text-[#c4953a] hover:bg-[#c4953a]/10 transition-colors disabled:opacity-40"
                        >
                          Set
                        </button>
                      </div>
                      {/* EF-134e: warn when the BE auto-fill price is above current market —
                          the stop would trigger immediately on submit. Informational only;
                          the Set button stays enabled in case the user wants this on purpose. */}
                      {beActive && breakEven !== null && currentPrice > 0 && breakEven > currentPrice && (
                        <div className="px-3 mt-1">
                          <span className="font-mono text-[8px] text-[#c4953a]/70">
                            ⚠ BE ${breakEven.toFixed(2)} is above market ${currentPrice.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {moveStopWarning && (
                        <div className="px-3 mt-1.5">
                          <span className="font-mono text-[9px] text-[#c4953a] animate-pulse">
                            ⚠ {moveStopWarning}
                          </span>
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>
            )
          })}
        </div>

      </div>

      {/* Confirmation Modal */}
      <OrderConfirmationModal
        open={confirmState.open}
        onClose={cancelConfirmation}
        onConfirm={confirmOrder}
        loading={confirmState.loading}
        action={confirmState.action}
        symbol={confirmState.symbol}
        orders={confirmState.orders}
        results={confirmState.results}
        onReconnect={brokerConn.reconnect}
        onPostReconnectRefresh={brokerConn.refresh}
      />
    </div>
  )
}
